/* eslint-disable react/forbid-prop-types */
import React, { useMemo, useState } from 'react';
import { FiInfo } from 'react-icons/fi';
import PropTypes from 'prop-types';
import AppButton from '../../../../shared/components/app-button/AppButton';
import AppTooltip from '../../../../shared/components/app-tool-tip/AppTooltip';
import { CABIN_META_ORDER } from '../../constants/cruiseConstants';
import {
  categoryStatus,
  decksLabel,
  formatAmount,
  metaLabel,
  sizeLabel,
  toNumber,
  titleCase,
  wholePartyTotal,
} from '../../utils/cruiseUtils';

const ALL = 'all';

const minPrice = (list) =>
  list.length ? Math.min(...list.map(wholePartyTotal)) : 0;

/** A category whose staterooms can't hold the whole party. */
export const tooSmallFor = (category, partySize) =>
  toNumber(category.totalBerths) > 0 &&
  partySize > toNumber(category.totalBerths);

function CruiseCategories({
  categories,
  partySize,
  selectedCode,
  onSelect,
  onContinue,
}) {
  const [meta, setMeta] = useState(ALL);

  const metaTabs = useMemo(() => {
    const present = CABIN_META_ORDER.filter((m) =>
      categories.some((c) => c.meta === m),
    );
    // Any meta code we have no label for still gets its own tab.
    categories.forEach((c) => {
      if (c.meta && !present.includes(c.meta)) present.push(c.meta);
    });
    return [ALL, ...present].map((m) => {
      const list =
        m === ALL ? categories : categories.filter((c) => c.meta === m);
      return {
        key: m,
        label: m === ALL ? 'All' : metaLabel(m),
        count: list.length,
        from: minPrice(list),
      };
    });
  }, [categories]);

  const list = useMemo(
    () =>
      [
        ...(meta === ALL
          ? categories
          : categories.filter((c) => c.meta === meta)),
      ].sort((a, b) => wholePartyTotal(a) - wholePartyTotal(b)),
    [categories, meta],
  );

  const selected = categories.find((c) => c.categoryCode === selectedCode);

  if (!categories.length) {
    return <div className="cr-empty">No cabin categories are available.</div>;
  }

  return (
    <div className="cr-categories">
      <div className="cr-meta-tabs">
        {metaTabs.map((t) => (
          <button
            type="button"
            key={t.key}
            className={`cr-select-tile ${meta === t.key ? 'is-selected' : ''}`}
            onClick={() => setMeta(t.key)}
            aria-pressed={meta === t.key}
          >
            <span className="cr-select-tile__title">
              {t.label} <small>({t.count})</small>
            </span>
            <span className="cr-select-tile__sub">
              from {formatAmount(t.from)}
            </span>
          </button>
        ))}
      </div>

      {list.map((c) => {
        const on = c.categoryCode === selectedCode;
        const status = categoryStatus(c.categoryStatus);
        const size = sizeLabel(
          c.smallestCabinSquareFeet,
          c.largestCabinSquareFeet,
        );
        const decks = decksLabel(c.deckNames);
        const full = tooSmallFor(c, partySize);
        const base = toNumber(c.wholeParty?.baseFare);
        const tax = toNumber(c.wholeParty?.taxFare);
        const pick = () => !full && onSelect(c);
        return (
          <div
            key={c.categoryCode}
            className={`cr-category ${on ? 'is-selected' : ''} ${
              full ? 'is-disabled' : ''
            }`}
            role="button"
            tabIndex={full ? -1 : 0}
            aria-disabled={full}
            onClick={pick}
            onKeyDown={(e) => e.key === 'Enter' && pick()}
          >
            <span className="cr-code-tile">{c.categoryCode}</span>
            <div className="cr-category__info">
              <div className="cr-category__name">
                <b>{titleCase(c.categoryName)}</b>
                {status && (
                  <AppTooltip
                    tooltipClassName="cr-status-tip"
                    title={
                      <span className={`cr-badge cr-badge--${status.tone}`}>
                        {status.label}
                        {status.tip && <FiInfo size={11} />}
                      </span>
                    }
                    content={status.tip || status.label}
                  />
                )}
                {c.nonRefundableFlag === 'Y' && (
                  <span className="cr-badge cr-badge--danger">
                    Non-refundable
                  </span>
                )}
              </div>
              <div className="cr-category__facts">
                {size && <span>{size}</span>}
                {c.totalBerths > 0 && (
                  <span className={full ? 'cr-text-danger' : ''}>
                    Up to {c.totalBerths} guests
                    {full ? ` · your party has ${partySize}` : ''}
                  </span>
                )}
                {decks && <span>Decks: {decks}</span>}
              </div>
            </div>
            <div className="cr-category__price">
              <span className="cr-amount">
                {formatAmount(wholePartyTotal(c))}
              </span>
              <span className="cr-muted-xs">whole party, incl. taxes</span>
              {(base > 0 || tax > 0) && (
                <span className="cr-category__split">
                  Fare {formatAmount(base)} + Taxes, fees &amp; port{' '}
                  {formatAmount(tax)}
                </span>
              )}
            </div>
            <AppButton
              btnText={on ? 'Selected' : 'Select'}
              variant={on ? 'primary' : 'outlined'}
              size="small"
              className="cr-category__btn"
              disabled={full}
              onClick={(e) => {
                e.stopPropagation();
                pick();
              }}
              style={{ height: '38px', minWidth: '96px', fontWeight: 600 }}
            />
          </div>
        );
      })}

      {selected && (
        <div className="cr-sticky-bar cr-sticky-bar--mobile-fixed">
          <div className="cr-sticky-bar__info">
            <div className="cr-muted-sm">Selected category</div>
            <b>
              {selected.categoryCode} · {titleCase(selected.categoryName)}
            </b>
          </div>
          <div className="cr-sticky-bar__price">
            <div className="cr-muted-sm">Whole party, incl. taxes</div>
            <b className="cr-amount cr-amount--md">
              {formatAmount(wholePartyTotal(selected))}
            </b>
          </div>
          <AppButton
            btnText="Select Cabin"
            onClick={onContinue}
            style={{ height: '44px', padding: '0 24px', fontWeight: 600 }}
          />
        </div>
      )}
    </div>
  );
}

CruiseCategories.propTypes = {
  categories: PropTypes.array.isRequired,
  partySize: PropTypes.number.isRequired,
  selectedCode: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
};

CruiseCategories.defaultProps = {
  selectedCode: '',
};

export default CruiseCategories;
